import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import type { LeadSource } from '@interfaces/ILogistics';
import { leadEntryValid } from '@d-orca/shared/client/logistics';
import { InfoTip } from '@components/ui/InfoTip/InfoTip';
import styles from './StockEntryDialog.module.css';

interface IStockEntryLeadFieldProps {
  value: string;
  onChange: (value: string) => void;
  className: string;
  seedNote: ReactNode;
  leadUntouched: boolean;
  openLeadSource: LeadSource | 'none';
  openLeadDays: number | null;
  t: TFunction;
}

/** Directly under the material it belongs to. Always present: it is the
 *  number the reorder point is derived from, and hiding it where the system had
 *  an opinion left that opinion invisible and uncorrectable. */
export function StockEntryLeadField({
  value,
  onChange,
  className,
  seedNote,
  leadUntouched,
  openLeadSource,
  openLeadDays,
  t,
}: IStockEntryLeadFieldProps) {
  return (
    <div className={styles.field}>
      <div className={styles.labelRow}>
        <label className={styles.label} htmlFor="stock-lead">
          {t('logistics.stockDialog.leadLabel')}
        </label>
        <InfoTip text={t('logistics.stockDialog.leadInfo')} />
      </div>
      <div className={styles.spanRow}>
        <input
          id="stock-lead"
          className={className}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputMode="numeric"
          autoComplete="off"
        />
        <span className={styles.spanCaption}>{t('logistics.stockDialog.days')}</span>
      </div>
      {/* The Save rule's own check, said here so a refused lead is not just
          a greyed-out Save. */}
      {value.trim() !== '' && !leadEntryValid(value) ? (
        <span className={styles.fieldWarn} role="alert">
          {t('logistics.stockDialog.leadInvalid')}
        </span>
      ) : null}
      {seedNote}
      {/* Where the number came from, and what changing it does. The
          three sources render as an identical figure, so without this
          a site average borrowed from other materials reads exactly
          like a measurement of this one. */}
      <span className={styles.leadHint}>
        {value.trim() !== '' && !leadUntouched
          ? t('logistics.stockDialog.leadHint')
          : t(`logistics.stockDialog.leadWhy.${openLeadSource}`, { n: openLeadDays })}
      </span>
    </div>
  );
}
